import React, { useRef } from 'react';
import { formatCurrency, formatNumber } from '../../utils/formatters';
import { FaPrint, FaReceipt } from 'react-icons/fa';
import { useQZTray } from '../../context/QZTrayContext';
import { useTranslation } from '../../context/LanguageContext';
import createTimeReportThermal from './thermal-templates/TimeReportThermal';
import qz from 'qz-tray';

// Print-specific styles
const printStyles = `
    @media print {
        body * {
            visibility: hidden;
        }
        .time-report,
        .time-report * {
            visibility: visible;
        }
        .time-report {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
        }
        .no-print {
            display: none !important;
        }
        table {
            page-break-inside: auto;
        }
        tr {
            page-break-inside: avoid;
            page-break-after: auto;
        }
        thead {
            display: table-header-group;
        }
    }
`;

const TimeReport = ({ data, language, settings }) => {
    // eslint-disable-next-line no-unused-vars
    const { t } = useTranslation();
    const { isConnected, connectQZ, printers } = useQZTray();
    // eslint-disable-next-line no-unused-vars
    const printerRef = useRef(null);

    if (!data?.hourly_breakdown || !data?.summary) return null;

    // Ensure returns data exists
    const returns = data.returns || { total_amount: 0, count: 0, percentage: 0 };
    
    // Process return orders data if available
    const hasReturns = returns && (returns.count > 0 || returns.total_amount > 0);

    // Debug returns data


    const handlePrint = () => {
        window.print();
    };

    const handleThermalPrint = async () => {
        if (!settings || !settings.printing || !settings.printing.printer_name) {
            console.error('Printer not configured in settings');
            return;
        }

        if (!isConnected) {
            try {
                await connectQZ();
            } catch (err) {
                console.error('Failed to connect to printer:', err);
                return;
            }
        }

        // Debug returns data before thermal printing


        const thermalContent = createTimeReportThermal(data, language, settings);

        const { printing } = settings;
        const paperWidth = printing.paper_size || '80mm';
        const paperHeight = printing.paper_height || 'auto';
        // eslint-disable-next-line no-unused-vars
        const fontSize = printing.font_size || '10';

        // Calculate dimensions for printer config
        let pageWidth = parseInt(paperWidth);
        if (isNaN(pageWidth)) {
            console.error('Invalid paper width:', paperWidth);
            pageWidth = 80; // Use 80mm as default width
        }
        
        // Parse height properly - critical for preventing infinite printing
        let pageHeight;
        if (paperHeight === 'auto') {
            pageHeight = 300; // Use 300mm as default height for 'auto'

        } else {
            pageHeight = parseInt(paperHeight.replace('mm', ''));
            if (isNaN(pageHeight)) {
                console.error('Invalid paper height:', paperHeight);
                pageHeight = 300; // Use 300mm as default height
            }
        }


        // Find the matching printer
        const matchingPrinter = printers.find(printer => 
            printer.value === printing.printer_name || 
            printer.label === printing.printer_name
        );

        if (!matchingPrinter) {
            console.error(`Selected printer "${printing.printer_name}" not found in available printers:`, printers);
            return;
        }

        // Create base printer config
        const config = qz.configs.create(matchingPrinter.value);
        
        // Set essential print options - this is critical for fixing infinite printing
        config.reconfigure({
            size: { width: pageWidth, height: pageHeight },
            margins: { top: 0, right: 2, bottom: 0, left: 2 },
            colorType: 'grayscale',
            orientation: 'portrait',
            units: 'mm',
            density: '180dpi',
            scaleContent: true, // Set to true to ensure content fits properly
            bounds: {
                width: pageWidth,
                height: pageHeight
            }
        });

        // Create print data
        const printData = [{
            type: 'html',
            format: 'plain',
            data: thermalContent,
            options: {
                pageWidth: `${pageWidth}mm`,
                pageHeight: `${pageHeight}mm`,
                css: true
            }
        }];

        // Print the report
        qz.print(config, printData)
            .then(() => {
    
            })
            .catch(err => {
                console.error('[TimeReport] Print failed:', err);
            });
    };

    // Format hour function
    const formatHour = (hour) => {
        if (language === 'ar') {
            // Convert to Arabic numerals and format
            const toArabicNumeral = (num) => num.toString().replace(/\d/g, d => '٠١٢٣٤٥٦٧٨٩'[d]);
            const period = hour >= 12 ? 'م' : 'ص';
            const displayHour = hour % 12 === 0 ? 12 : hour % 12;
            return `${toArabicNumeral(displayHour)} ${period}`;
        } else {
            // English format
            const period = hour >= 12 ? 'PM' : 'AM';
            const displayHour = hour % 12 === 0 ? 12 : hour % 12;
            return `${displayHour} ${period}`;
        }
    };

    return (
        <div className="time-report mt-8 space-y-8" dir={language === 'ar' ? 'rtl' : 'ltr'}>
            <style>{printStyles}</style>
            {/* Print Button */}
            <div className="flex justify-end space-x-4 no-print" style={{ direction: 'ltr' }}>
                <button
                    onClick={handleThermalPrint}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                    <FaReceipt className="mr-2" />
                    {language === 'ar' ? 'طباعة حرارية' : 'Thermal Print'}
                </button>
                <button
                    onClick={handlePrint}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                    <FaPrint className="mr-2" />
                    {language === 'ar' ? 'طباعة التقرير' : 'Print Report'}
                </button>
            </div>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-indigo-50 rounded-lg p-6">
                    <p className="text-sm text-gray-500">
                        {language === 'ar' ? 'ساعة الذروة' : 'Peak Hour'}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        {formatHour(data.summary.peak_hour.hour)}
                    </p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-6">
                    <p className="text-sm text-gray-500">
                        {language === 'ar' ? 'إجمالي الطلبات' : 'Total Orders'}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        {formatNumber(data.summary.total_orders)}
                    </p>
                </div>
                <div className="bg-indigo-50 rounded-lg p-6">
                    <p className="text-sm text-gray-500">
                        {language === 'ar' ? 'إجمالي المبيعات' : 'Total Revenue'}
                    </p>
                    <p className="text-2xl font-semibold text-gray-900">
                        {formatCurrency(data.summary.total_revenue)}
                    </p>
                </div>
            </div>

            {/* Return Orders Summary (if available) */}
            {hasReturns && (
                <div className="bg-red-50 rounded-lg p-6 border border-red-100">
                    <h3 className="text-lg font-semibold mb-3 text-red-700">
                        {language === 'ar' ? 'ملخص المرتجعات' : 'Returns Summary'}
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                            <p className="text-sm text-gray-500">
                                {language === 'ar' ? 'إجمالي المرتجعات' : "Total Returns Amount"}
                            </p>
                            <p className="text-xl font-semibold text-red-700">
                                {formatCurrency(returns.total_amount || 0)}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">
                                {language === 'ar' ? 'عدد الطلبات المرتجعة' : "Number of Returns"}
                            </p>
                            <p className="text-xl font-semibold text-red-700">
                                {formatNumber(returns.count || 0)}
                            </p>
                        </div>
                        <div>
                            <p className="text-sm text-gray-500">
                                {language === 'ar' ? 'نسبة المرتجعات' : "Returns Percentage"}
                            </p>
                            <p className="text-xl font-semibold text-red-700">
                                {returns.percentage ? `${returns.percentage.toFixed(2)}%` : '0%'}
                            </p>
                        </div>
                    </div>
                </div>
            )}

            {/* Hourly Analysis Table */}
            <div>
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {language === 'ar' ? 'تحليل المبيعات حسب الساعة' : 'Hourly Sales Analysis'}
                </h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'الساعة' : 'Hour'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'عدد الطلبات' : 'Orders'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'متوسط قيمة الطلب' : 'Avg Order'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'إجمالي المبيعات' : 'Revenue'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'أكثر المنتجات مبيعاً' : 'Popular Items'}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {data.hourly_breakdown.map((hour, index) => (
                                <tr key={hour.hour} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {formatHour(hour.hour)}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {formatNumber(hour.order_count)}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {formatCurrency(hour.avg_order_value)}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {formatCurrency(hour.total_revenue)}
                                    </td>
                                    <td className={`px-6 py-4 text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        <ul className="list-disc list-inside">
                                            {hour.popular_items && hour.popular_items.length > 0 ? (
                                                hour.popular_items.map((item, idx) => (
                                                    <li key={idx}>
                                                        {language === 'ar' ? item.name_ar : item.name} 
                                                        <span className="text-gray-500 ml-1">
                                                            ({formatNumber(item.total_quantity)})
                                                        </span>
                                                    </li>
                                                ))
                                            ) : (
                                                <span className="text-gray-500">
                                                    {language === 'ar' ? 'لا توجد منتجات' : 'No items'}
                                                </span>
                                            )}
                                        </ul>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Weekday Analysis Table */}
            <div className="mt-10">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                    {language === 'ar' ? 'تحليل المبيعات حسب أيام الأسبوع' : 'Weekday Sales Analysis'}
                </h3>
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'اليوم' : 'Day'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'عدد الطلبات' : 'Orders'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'متوسط قيمة الطلب' : 'Avg Order'}
                                </th>
                                <th className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                    {language === 'ar' ? 'إجمالي المبيعات' : 'Revenue'}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {data.weekday_analysis.map((day, index) => (
                                <tr key={day.day_number} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {language === 'ar' ? [
                                            'الإثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت', 'الأحد'
                                        ][day.day_number] : day.day_name}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {formatNumber(day.order_count)}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {formatCurrency(day.avg_order_value)}
                                    </td>
                                    <td className={`px-6 py-4 whitespace-nowrap text-sm text-gray-900 ${language === 'ar' ? 'text-right' : 'text-left'}`}>
                                        {formatCurrency(day.total_revenue)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default TimeReport;
